<script setup>
import { useId } from 'vue';

defineProps({
  channel: {
    type: String,
    required: true,
    validator: v =>
      [
        'whatsapp',
        'instagram',
        'telegram',
        'messenger',
        'twitter',
        'gmail',
        'outlook',
        'line',
        'tiktok',
        'email',
        'website',
        'phone',
      ].includes(v),
  },
  sizeClass: {
    type: String,
    default: 'size-[1em]',
  },
});

const igGradientId = `ig-grad-${useId()}`;
</script>

<template>
  <!-- WhatsApp -->
  <svg
    v-if="channel === 'whatsapp'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
      fill="#25D366"
    />
    <path
      d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.96 7.96 0 01-4.108-1.14l-.288-.17-2.884.856.812-2.97-.188-.3A7.96 7.96 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"
      fill="#25D366"
    />
  </svg>

  <!-- Instagram -->
  <svg
    v-else-if="channel === 'instagram'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <defs>
      <radialGradient :id="igGradientId" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stop-color="#fdf497" />
        <stop offset="5%" stop-color="#fdf497" />
        <stop offset="45%" stop-color="#fd5949" />
        <stop offset="60%" stop-color="#d6249f" />
        <stop offset="90%" stop-color="#285AEB" />
      </radialGradient>
    </defs>
    <rect
      x="2"
      y="2"
      width="20"
      height="20"
      rx="6"
      :stroke="`url(#${igGradientId})`"
      stroke-width="2"
      fill="none"
    />
    <circle
      cx="12"
      cy="12"
      r="4.5"
      :stroke="`url(#${igGradientId})`"
      stroke-width="2"
      fill="none"
    />
    <circle cx="17.5" cy="6.5" r="1.25" :fill="`url(#${igGradientId})`" />
  </svg>

  <!-- Telegram -->
  <svg
    v-else-if="channel === 'telegram'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.28-.02-.12.02-2.02 1.28-5.69 3.77-.54.37-1.03.55-1.47.54-.48-.01-1.41-.27-2.1-.5-.85-.28-1.52-.43-1.46-.91.03-.25.38-.51 1.05-.78 4.12-1.79 6.87-2.97 8.26-3.54 3.93-1.62 4.75-1.9 5.28-1.91.12 0 .38.03.55.17.14.12.18.28.2.45-.01.06.01.24 0 .37z"
      fill="#26A5E4"
    />
  </svg>

  <!-- Messenger -->
  <svg
    v-else-if="channel === 'messenger'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.82c.02.64.68 1.05 1.26.79l2.04-.9c.17-.08.37-.1.55-.06.6.16 1.24.25 1.9.25H12c5.64 0 10-4.13 10-9.7S17.64 2 12 2z"
      fill="#0084FF"
    />
    <path
      d="M6.53 14.22l2.68-4.25c.43-.68 1.33-.85 1.96-.37l2.13 1.6c.2.15.47.15.66 0l2.88-2.19c.38-.29.88.18.62.59l-2.68 4.25c-.43.68-1.33.85-1.96.37l-2.13-1.6a.53.53 0 00-.66 0l-2.88 2.19c-.38.29-.88-.18-.62-.59z"
      fill="white"
    />
  </svg>

  <!-- X (Twitter) -->
  <svg
    v-else-if="channel === 'twitter'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      fill="currentColor"
    />
  </svg>

  <!-- Gmail -->
  <svg
    v-else-if="channel === 'gmail'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M22 6.25V18a2 2 0 01-2 2H4a2 2 0 01-2-2V6.25l10 6.5 10-6.5z"
      fill="#D14836"
      opacity="0.2"
    />
    <path d="M22 4H2L12 10.75 22 4z" fill="#D14836" />
    <path
      d="M2 6.25V18a2 2 0 002 2h16a2 2 0 002-2V6.25l-10 6.5-10-6.5z"
      stroke="#D14836"
      stroke-width="0.5"
      fill="none"
    />
  </svg>

  <!-- Outlook -->
  <svg
    v-else-if="channel === 'outlook'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M22 6.25V18a2 2 0 01-2 2H4a2 2 0 01-2-2V6.25l10 6.5 10-6.5z"
      fill="#0078D4"
      opacity="0.2"
    />
    <path d="M22 4H2L12 10.75 22 4z" fill="#0078D4" />
    <path
      d="M2 6.25V18a2 2 0 002 2h16a2 2 0 002-2V6.25l-10 6.5-10-6.5z"
      stroke="#0078D4"
      stroke-width="0.5"
      fill="none"
    />
  </svg>

  <!-- LINE -->
  <svg
    v-else-if="channel === 'line'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M12 2C6.48 2 2 5.81 2 10.5c0 4.01 3.17 7.39 7.62 8.23.3.06.7.2.8.44.1.22.06.56.03.78l-.13.78c-.04.23-.18.9.79.49s5.24-3.08 7.15-5.27C20.29 13.72 22 12.23 22 10.5 22 5.81 17.52 2 12 2z"
      fill="#06C755"
    />
  </svg>

  <!-- TikTok -->
  <svg
    v-else-if="channel === 'tiktok'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M16.6 5.82A4.28 4.28 0 0115.05 3h-3.12v12.4a2.59 2.59 0 01-2.59 2.5c-1.43 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.07 2.53 5.56 5.6 5.56 3.1 0 5.6-2.5 5.6-5.6V9.35a7.37 7.37 0 004.33 1.39V7.64a4.28 4.28 0 01-2.57-1.82z"
      fill="currentColor"
    />
  </svg>

  <!-- Email (generic) -->
  <svg
    v-else-if="channel === 'email'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M22 6.25V18a2 2 0 01-2 2H4a2 2 0 01-2-2V6.25l10 6.5 10-6.5z"
      fill="#D14836"
      opacity="0.2"
    />
    <path d="M22 4H2L12 10.75 22 4z" fill="#D14836" />
    <path
      d="M2 6.25V18a2 2 0 002 2h16a2 2 0 002-2V6.25l-10 6.5-10-6.5z"
      stroke="#D14836"
      stroke-width="0.5"
      fill="none"
    />
  </svg>

  <!-- Website -->
  <svg
    v-else-if="channel === 'website'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <circle
      cx="12"
      cy="12"
      r="9.5"
      stroke="#2E86C1"
      stroke-width="1.5"
      fill="none"
    />
    <ellipse
      cx="12"
      cy="12"
      rx="4"
      ry="9.5"
      stroke="#2E86C1"
      stroke-width="1.5"
      fill="none"
    />
    <path d="M3 12h18" stroke="#2E86C1" stroke-width="1.5" />
    <path
      d="M4.5 7.5h15M4.5 16.5h15"
      stroke="#2E86C1"
      stroke-width="1"
      opacity="0.5"
    />
  </svg>

  <!-- Phone (Voice/Telefonia) -->
  <svg
    v-else-if="channel === 'phone'"
    viewBox="0 0 24 24"
    fill="none"
    :class="sizeClass"
  >
    <path
      d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"
      fill="currentColor"
    />
  </svg>
</template>
